import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ClientApiError } from "../auth/api";
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Skeleton,
  Textarea,
  Toast,
} from "../components/ui";
import {
  checkDogDuplicates,
  createDog,
  getMyDog,
  listMyDogs,
  submitDog,
  updateDog,
  type DogDetail,
  type DogInput,
  type DogSummary,
  type DuplicateResult,
} from "../dogs/api";
import { DogStatusBadge } from "../dogs/DogStatus";
import { TaxonomyAutocomplete } from "../taxonomy/TaxonomyAutocomplete";
import type { PublicTaxonomyItem } from "../taxonomy/api";

const dateFormatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "medium",
  timeZone: "Asia/Ho_Chi_Minh",
});

function formatDate(value: string | null): string {
  if (!value) return "Chưa cập nhật";
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(parsed.getTime()) ? value : dateFormatter.format(parsed);
}

function problemFrom(caught: unknown): { message: string; requestId?: string; fields?: Record<string, string> } {
  if (caught instanceof ClientApiError) {
    return {
      message: caught.problem.message,
      requestId: caught.problem.requestId,
      fields: caught.problem.fieldErrors,
    };
  }
  return { message: "Không thể xử lý yêu cầu. Vui lòng thử lại." };
}

export function MemberDogsPage() {
  const [items, setItems] = useState<DogSummary[]>([]);
  const [status, setStatus] = useState("all");
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listMyDogs({ status: status as Parameters<typeof listMyDogs>[0]["status"], cursor, limit: 20 })
      .then((page) => {
        if (!active) return;
        setItems(page.items);
        setNextCursor(page.nextCursor);
        setError(null);
      })
      .catch((caught) => {
        if (active) setError(problemFrom(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cursor, status]);

  return (
    <section className="dog-member-page">
      <header className="page-heading dog-page-heading">
        <div>
          <p className="eyebrow">Dog Core</p>
          <h2>Hồ sơ chó của tôi</h2>
          <p>Tạo bản nháp, kiểm tra trùng dữ liệu và gửi kiểm duyệt.</p>
        </div>
        <Link className="ui-button ui-button--primary ui-button--md" to="/member/dogs/new">
          Tạo hồ sơ chó
        </Link>
      </header>

      <div className="dog-list-toolbar">
        <Select
          label="Trạng thái"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setCursor(null);
          }}
        >
          <option value="all">Tất cả</option>
          <option value="draft">Bản nháp</option>
          <option value="pending_review">Chờ duyệt</option>
          <option value="published">Đã công khai</option>
          <option value="needs_changes">Cần chỉnh sửa</option>
          <option value="disputed">Đang tranh chấp</option>
          <option value="rejected">Bị từ chối</option>
          <option value="archived">Đã lưu trữ</option>
        </Select>
      </div>

      {loading ? <Skeleton lines={5} /> : null}
      {!loading && error ? (
        <ErrorState
          title="Không tải được hồ sơ"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Thử lại", onClick: () => setCursor((value) => value) }}
        />
      ) : null}
      {!loading && !error && items.length === 0 ? (
        <EmptyState
          title="Chưa có hồ sơ chó"
          description="Hồ sơ bắt đầu ở trạng thái draft và không xuất hiện công khai trước khi được duyệt."
        />
      ) : null}
      {!loading && !error && items.length > 0 ? (
        <div className="dog-member-list">
          {items.map((dog) => (
            <article className="dog-member-card" key={dog.id}>
              <div className="dog-avatar-placeholder" aria-hidden="true">
                {dog.registeredName.slice(0, 1).toUpperCase()}
              </div>
              <div className="dog-member-card__body">
                <div className="dog-card-title">
                  <div>
                    <h3>{dog.registeredName}</h3>
                    <p>{dog.callName ? `Tên gọi: ${dog.callName}` : "Chưa có tên gọi"}</p>
                  </div>
                  <DogStatusBadge status={dog.workflowStatus} />
                </div>
                <dl className="dog-quick-grid dog-quick-grid--compact">
                  <div><dt>Giống</dt><dd>{dog.breed?.nameVi ?? "Chưa rõ"}</dd></div>
                  <div><dt>Giới tính</dt><dd>{dog.sex === "male" ? "Đực" : dog.sex === "female" ? "Cái" : "Chưa rõ"}</dd></div>
                  <div><dt>Ngày sinh</dt><dd>{formatDate(dog.dateOfBirth)}</dd></div>
                  <div><dt>Cập nhật</dt><dd>{formatDate(dog.updatedAt)}</dd></div>
                </dl>
                <div className="button-row">
                  <Link className="ui-button ui-button--secondary ui-button--sm" to={`/member/dogs/${dog.id}/edit`}>
                    Mở hồ sơ
                  </Link>
                  {dog.workflowStatus === "published" ? (
                    <Link className="text-link" to={`/dogs/${dog.slug}`}>Xem công khai</Link>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : null}

      <div className="dog-cursor-pagination">
        <Button variant="secondary" disabled={!cursor} onClick={() => setCursor(null)}>Trang đầu</Button>
        <Button variant="secondary" disabled={!nextCursor} onClick={() => setCursor(nextCursor)}>Trang sau</Button>
      </div>
    </section>
  );
}

interface DogFormState {
  registeredName: string;
  callName: string;
  breed: PublicTaxonomyItem | null;
  sex: "male" | "female" | "unknown";
  dateOfBirth: string;
  dateOfDeath: string;
  datePrecision: "day" | "month" | "year" | "unknown";
  color: PublicTaxonomyItem | null;
  country: PublicTaxonomyItem | null;
  province: PublicTaxonomyItem | null;
  registrationOrganization: PublicTaxonomyItem | null;
  registrationNumber: string;
  pedigreeNumber: string;
  microchipNumber: string;
  breederName: string;
  kennelId: string;
  sire: DogSummary | null;
  dam: DogSummary | null;
  sourceType: "member" | "kennel" | "registry_import" | "moderator" | "legacy";
  sourceNote: string;
  biography: string;
  visibility: "public" | "members" | "private";
}

const EMPTY_FORM: DogFormState = {
  registeredName: "",
  callName: "",
  breed: null,
  sex: "unknown",
  dateOfBirth: "",
  dateOfDeath: "",
  datePrecision: "unknown",
  color: null,
  country: null,
  province: null,
  registrationOrganization: null,
  registrationNumber: "",
  pedigreeNumber: "",
  microchipNumber: "",
  breederName: "",
  kennelId: "",
  sire: null,
  dam: null,
  sourceType: "member",
  sourceNote: "",
  biography: "",
  visibility: "public",
};

function detailToForm(dog: DogDetail): DogFormState {
  return {
    registeredName: dog.registeredName,
    callName: dog.callName ?? "",
    breed: dog.breed,
    sex: dog.sex,
    dateOfBirth: dog.dateOfBirth ?? "",
    dateOfDeath: "",
    datePrecision: dog.dateOfBirth ? "day" : "unknown",
    color: dog.color,
    country: dog.locationCountry,
    province: dog.locationProvince,
    registrationOrganization: dog.registrationOrganization,
    registrationNumber: dog.registrationNumber ?? "",
    pedigreeNumber: dog.pedigreeNumber ?? "",
    microchipNumber: dog.microchipNumber ?? "",
    breederName: dog.breederName ?? "",
    kennelId: dog.kennel?.id ?? "",
    sire: dog.sire ? {
      id: dog.sire.id,
      slug: dog.sire.slug,
      registeredName: dog.sire.registeredName,
      callName: null,
      breed: null,
      sex: "male",
      dateOfBirth: null,
      workflowStatus: dog.sire.workflowStatus,
      visibility: "public",
      version: 1,
      updatedAt: "",
      submittedAt: null,
      publishedAt: null,
    } : null,
    dam: dog.dam ? {
      id: dog.dam.id,
      slug: dog.dam.slug,
      registeredName: dog.dam.registeredName,
      callName: null,
      breed: null,
      sex: "female",
      dateOfBirth: null,
      workflowStatus: dog.dam.workflowStatus,
      visibility: "public",
      version: 1,
      updatedAt: "",
      submittedAt: null,
      publishedAt: null,
    } : null,
    sourceType: dog.sourceType,
    sourceNote: dog.sourceNote ?? "",
    biography: dog.biography ?? "",
    visibility: dog.visibility,
  };
}

function formToInput(form: DogFormState): DogInput {
  return {
    registeredName: form.registeredName,
    callName: form.callName || null,
    breedId: form.breed?.id ?? "",
    sex: form.sex,
    dateOfBirth: form.dateOfBirth || null,
    datePrecision: form.datePrecision,
    dateOfDeath: form.dateOfDeath || null,
    colorId: form.color?.id ?? null,
    locationCountryId: form.country?.id ?? null,
    locationProvinceId: form.province?.id ?? null,
    registrationOrganizationId: form.registrationOrganization?.id ?? null,
    registrationNumber: form.registrationNumber || null,
    pedigreeNumber: form.pedigreeNumber || null,
    microchipNumber: form.microchipNumber || null,
    breederName: form.breederName || null,
    kennelId: form.kennelId || null,
    sourceType: form.sourceType,
    sourceNote: form.sourceNote || null,
    biography: form.biography || null,
    visibility: form.visibility,
  };
}

function DuplicatePanel({ duplicate }: { duplicate: DuplicateResult }) {
  return (
    <section className={`dog-duplicate-panel dog-duplicate-panel--${duplicate.decision}`} aria-live="polite">
      <header>
        <div>
          <p className="eyebrow">Duplicate detection</p>
          <h3>
            {duplicate.decision === "clear"
              ? "Không thấy hồ sơ trùng đáng kể"
              : duplicate.decision === "review"
                ? "Cần kiểm tra các hồ sơ tương tự"
                : "Có hồ sơ gần như chắc chắn đã tồn tại"}
          </h3>
        </div>
        <span>{duplicate.candidates.length} candidate</span>
      </header>
      {duplicate.candidates.map((candidate) => (
        <article key={candidate.dog.id} className="dog-duplicate-candidate">
          <div>
            <strong>{candidate.dog.registeredName}</strong>
            <small>{candidate.dog.breed?.nameVi ?? "Chưa rõ giống"} · điểm {candidate.score}</small>
          </div>
          <ul>
            {candidate.reasons.map((reason) => (
              <li key={reason.code}>{reason.labelVi} (+{reason.points})</li>
            ))}
          </ul>
          {candidate.dog.workflowStatus === "published" ? (
            <Link className="text-link" to={`/dogs/${candidate.dog.slug}`}>Mở hồ sơ hiện có</Link>
          ) : null}
        </article>
      ))}
      {duplicate.decision === "block" ? (
        <p className="dog-duplicate-guidance">
          Không tạo bản ghi mới. Mở hồ sơ hiện có để gửi ownership claim hoặc edit request.
        </p>
      ) : null}
    </section>
  );
}

export function DogEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [dog, setDog] = useState<DogDetail | null>(null);
  const [form, setForm] = useState<DogFormState>(EMPTY_FORM);
  const [duplicate, setDuplicate] = useState<DuplicateResult | null>(null);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; requestId?: string; fields?: Record<string, string> } | null>(null);
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    getMyDog(id)
      .then(({ dog: value }) => {
        if (!active) return;
        setDog(value);
        setForm(detailToForm(value));
      })
      .catch((caught) => {
        if (active) setError(problemFrom(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const locked = dog?.workflowStatus === "pending_review" || dog?.workflowStatus === "disputed";
  const canSubmit = dog && ["draft", "needs_changes"].includes(dog.workflowStatus);
  const title = editing ? `Chỉnh sửa ${dog?.registeredName ?? "hồ sơ chó"}` : "Tạo hồ sơ chó";
  const sensitiveNotice = dog?.workflowStatus === "published"
    ? "Tên đăng ký, giống, ngày sinh, số đăng ký, microchip và cha/mẹ sẽ tạo edit request để kiểm duyệt."
    : null;

  function set<K extends keyof DogFormState>(key: K, value: DogFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function checkDuplicates() {
    setSaving(true);
    setError(null);
    try {
      const response = await checkDogDuplicates({ ...formToInput(form), excludeDogId: id ?? null });
      setDuplicate(response.duplicate);
      return response.duplicate;
    } catch (caught) {
      setError(problemFrom(caught));
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (!editing) {
        const checked = duplicate ?? await checkDogDuplicates();
        if (!checked || checked.decision === "block") return;
        const result = await createDog(formToInput(form));
        setDog(result.dog);
        setDuplicate(result.duplicate);
        setToast({ title: "Đã tạo draft", message: "Hồ sơ chưa xuất hiện công khai cho đến khi được duyệt." });
        navigate(`/member/dogs/${result.dog.id}/edit`, { replace: true });
      } else if (dog) {
        const result = await updateDog(dog.id, { ...formToInput(form), expectedVersion: dog.version });
        setDog(result.dog);
        setForm(detailToForm(result.dog));
        setDuplicate(result.duplicate);
        setToast({
          title: result.editRequestId ? "Đã gửi edit request" : "Đã lưu hồ sơ",
          message: result.editRequestId
            ? "Field trọng yếu sẽ chỉ thay đổi sau khi moderator duyệt."
            : "Dữ liệu đã được cập nhật.",
        });
      }
    } catch (caught) {
      setError(problemFrom(caught));
    } finally {
      setSaving(false);
    }
  }

  async function submit() {
    if (!dog) return;
    setSaving(true);
    setError(null);
    try {
      const result = await submitDog(dog.id, dog.version);
      setDog(result.dog);
      setDuplicate(result.duplicate);
      setToast({ title: "Đã gửi kiểm duyệt", message: "Hồ sơ đang khóa chỉnh sửa cho đến khi có kết quả." });
    } catch (caught) {
      setError(problemFrom(caught));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Skeleton lines={9} />;
  if (editing && error && !dog) {
    return <ErrorState title="Không mở được hồ sơ" description={error.message} requestId={error.requestId} />;
  }

  return (
    <section className="dog-editor-page">
      <header className="page-heading dog-page-heading">
        <div>
          <p className="eyebrow">Dog Core · Draft workflow</p>
          <h2>{title}</h2>
          <p>Thông tin nhận dạng và phả hệ được ưu tiên trước ảnh/gallery.</p>
        </div>
        {dog ? <DogStatusBadge status={dog.workflowStatus} /> : null}
      </header>

      {dog?.reviewNote ? (
        <div className="dog-review-note" role="status">
          <strong>Ghi chú kiểm duyệt</strong>
          <p>{dog.reviewNote}</p>
        </div>
      ) : null}
      {sensitiveNotice ? <p className="dog-sensitive-notice">{sensitiveNotice}</p> : null}
      {locked ? (
        <div className="dog-review-note">
          <strong>Hồ sơ đang khóa</strong>
          <p>Không thể chỉnh sửa khi hồ sơ đang chờ duyệt hoặc tranh chấp.</p>
        </div>
      ) : null}
      {error ? (
        <ErrorState
          title="Không lưu được hồ sơ"
          description={error.message}
          requestId={error.requestId}
        />
      ) : null}

      <fieldset disabled={locked || saving} className="dog-editor-fieldset">
        <legend>Thông tin cốt lõi</legend>
        <div className="dog-form-section">
          <h3>Nhận dạng</h3>
          <div className="form-grid">
            <Input
              label="Tên đăng ký"
              value={form.registeredName}
              error={error?.fields?.registeredName}
              onChange={(event) => set("registeredName", event.target.value)}
              required
            />
            <Input
              label="Tên gọi"
              value={form.callName}
              onChange={(event) => set("callName", event.target.value)}
            />
            <TaxonomyAutocomplete
              namespace="breed"
              label="Giống chó"
              hint="Dữ liệu lấy từ taxonomy quản trị."
              value={form.breed}
              onSelect={(item) => set("breed", item)}
            />
            <Select label="Giới tính" value={form.sex} onChange={(event) => set("sex", event.target.value as DogFormState["sex"])}>
              <option value="unknown">Chưa rõ</option>
              <option value="male">Đực</option>
              <option value="female">Cái</option>
            </Select>
            <Input label="Ngày sinh" type="date" value={form.dateOfBirth} onChange={(event) => set("dateOfBirth", event.target.value)} />
            <Select label="Độ chính xác ngày sinh" value={form.datePrecision} onChange={(event) => set("datePrecision", event.target.value as DogFormState["datePrecision"])}>
              <option value="unknown">Chưa rõ</option>
              <option value="year">Chỉ năm</option>
              <option value="month">Tháng và năm</option>
              <option value="day">Đủ ngày</option>
            </Select>
            <Input label="Ngày mất" type="date" value={form.dateOfDeath} onChange={(event) => set("dateOfDeath", event.target.value)} />
            <TaxonomyAutocomplete namespace="coat_color" label="Màu lông" value={form.color} onSelect={(item) => set("color", item)} />
          </div>
        </div>

        <div className="dog-form-section">
          <h3>Cha mẹ và nguồn</h3>
          <p>
            Quan hệ cha/mẹ được quản lý riêng bằng Pedigree Engine để có source, version và
            kiểm duyệt. {dog ? <Link to={`/member/dogs/${dog.id}/pedigree`}>Mở phả hệ hồ sơ này</Link> : "Hãy lưu draft trước khi thêm cha/mẹ."}
          </p>
          <div className="form-grid">
            <Input label="Người/kennel gây giống" value={form.breederName} onChange={(event) => set("breederName", event.target.value)} />
            <Input label="Kennel ID" hint="Chỉ kennel bạn có quyền quản lý." value={form.kennelId} onChange={(event) => set("kennelId", event.target.value)} />
            <Select label="Nguồn dữ liệu" value={form.sourceType} onChange={(event) => set("sourceType", event.target.value as DogFormState["sourceType"])}>
              <option value="member">Thành viên cung cấp</option>
              <option value="kennel">Kennel cung cấp</option>
              <option value="registry_import">Nhập từ tổ chức đăng ký</option>
            </Select>
            <Textarea label="Ghi chú nguồn" value={form.sourceNote} onChange={(event) => set("sourceNote", event.target.value)} />
          </div>
        </div>

        <div className="dog-form-section">
          <h3>Đăng ký và định danh</h3>
          <div className="form-grid">
            <TaxonomyAutocomplete
              namespace="registration_organization"
              label="Tổ chức đăng ký"
              value={form.registrationOrganization}
              onSelect={(item) => set("registrationOrganization", item)}
            />
            <Input label="Số đăng ký" value={form.registrationNumber} onChange={(event) => set("registrationNumber", event.target.value)} />
            <Input label="Số phả hệ" value={form.pedigreeNumber} onChange={(event) => set("pedigreeNumber", event.target.value)} />
            <Input label="Microchip" value={form.microchipNumber} onChange={(event) => set("microchipNumber", event.target.value)} autoComplete="off" />
          </div>
        </div>

        <div className="dog-form-section">
          <h3>Địa lý và hiển thị</h3>
          <div className="form-grid">
            <TaxonomyAutocomplete namespace="country" label="Quốc gia" value={form.country} onSelect={(item) => set("country", item)} />
            <TaxonomyAutocomplete namespace="province" label="Tỉnh/thành" value={form.province} onSelect={(item) => set("province", item)} />
            <Select label="Phạm vi hiển thị" value={form.visibility} onChange={(event) => set("visibility", event.target.value as DogFormState["visibility"])}>
              <option value="public">Công khai khi được duyệt</option>
              <option value="members">Chỉ thành viên</option>
              <option value="private">Riêng tư</option>
            </Select>
            <Textarea label="Giới thiệu ngắn" value={form.biography} onChange={(event) => set("biography", event.target.value)} />
          </div>
        </div>
      </fieldset>

      <div className="dog-editor-actions">
        <Button variant="secondary" disabled={saving || locked} onClick={() => void checkDuplicates()}>
          Kiểm tra hồ sơ trùng
        </Button>
        <Button disabled={saving || locked} onClick={() => void save()}>
          {saving ? "Đang xử lý…" : editing ? "Lưu thay đổi" : "Lưu bản nháp"}
        </Button>
        {canSubmit ? (
          <Button variant="primary" disabled={saving} onClick={() => void submit()}>
            Gửi kiểm duyệt
          </Button>
        ) : null}
      </div>

      {duplicate ? <DuplicatePanel duplicate={duplicate} /> : null}
      {toast ? <Toast title={toast.title} message={toast.message} onClose={() => setToast(null)} /> : null}
    </section>
  );
}
